import { useEffect, useState } from 'react';
import { evaluateChain, simulateNight } from '@neopolis/engine';
import type { ChainEvaluation, Content, GameState } from '@neopolis/engine';

import { T } from '../../textes-interface.js';
import { RueDeNuit } from './RueDeNuit.js';

/** Une heure de nuit toutes les 500 ms : assez lent pour être lu, assez court pour tenir en séance. */
const DUREE_PAR_HEURE = 500;

function Etoiles({ nombre }: { nombre: number }) {
  return (
    <p aria-label={`${T.tester.etoiles} : ${nombre} / 3`} className="text-2xl">
      <span aria-hidden="true">{'★'.repeat(nombre) + '☆'.repeat(3 - nombre)}</span>
    </p>
  );
}

/**
 * Étape 3 — Tester.
 *
 * « Simuler une nuit » fait défiler la rue de 18 h à 7 h. Le comportement du
 * lampadaire est calculé par le moteur à partir des équipements posés, et la
 * consommation se compare à celle de l'éclairage actuel.
 *
 * L'animation n'avance qu'une fois par heure simulée et laisse les transitions
 * au navigateur : rien n'est recalculé image par image, ce qui la garde fluide
 * sur une tablette d'entrée de gamme.
 */
export function EtapeTester({
  content,
  partie,
  onDeployer,
}: {
  content: Content;
  partie: GameState;
  onDeployer: () => void;
}) {
  const [index, setIndex] = useState<number | null>(null);
  const nuit = simulateNight(partie, content);
  const evaluation: ChainEvaluation = evaluateChain(partie, content, 'eclairage_nuit');

  const enCours = index !== null && index < nuit.moments.length - 1;

  useEffect(() => {
    if (index === null || !enCours) {
      return undefined;
    }
    const minuteur = setTimeout(
      () => setIndex((precedent) => (precedent ?? 0) + 1),
      DUREE_PAR_HEURE,
    );
    return () => clearTimeout(minuteur);
  }, [index, enCours]);

  const moment = nuit.moments[index ?? 0] ?? nuit.moments[0]!;
  const termine = index !== null && !enCours;
  const dejaDeploye = partie.deployedSolutionId !== null;

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border-2 border-slate-200 bg-white p-4">
        <RueDeNuit moment={moment} />

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setIndex(0)}
            className="min-h-touch rounded-xl bg-sky-600 px-6 font-semibold text-white hover:bg-sky-700"
          >
            {index === null ? T.tester.simuler : T.tester.rejouer}
          </button>

          {enCours && <span className="text-slate-600">{T.tester.enCours}</span>}
        </div>
      </section>

      {termine && (
        <>
          <section
            aria-live="polite"
            className="rounded-2xl border-2 border-slate-200 bg-white p-4"
          >
            <h3 className="font-bold text-slate-900">{T.tester.consommation}</h3>
            <dl className="mt-2 grid gap-2 sm:grid-cols-2">
              <div className="rounded-xl bg-slate-100 p-3">
                <dt className="text-sm text-slate-600">{T.tester.avant}</dt>
                <dd className="text-2xl font-bold tabular-nums text-slate-800">
                  {nuit.consommationActuelle.toFixed(1)}
                </dd>
              </div>
              <div className="rounded-xl bg-emerald-50 p-3">
                <dt className="text-sm text-slate-600">{T.tester.apres}</dt>
                <dd className="text-2xl font-bold tabular-nums text-emerald-800">
                  {nuit.consommation.toFixed(1)}
                </dd>
              </div>
            </dl>
            <p className="mt-2 text-sm text-slate-600">{T.tester.unite}</p>
          </section>

          <section className="rounded-2xl border-2 border-sky-200 bg-sky-50 p-4">
            {evaluation.solution && <Etoiles nombre={evaluation.solution.etoiles} />}
            <ul className="mt-2 space-y-2">
              {evaluation.messages.map((message) => (
                <li key={message} className="text-slate-800">
                  {message}
                </li>
              ))}
            </ul>

            {evaluation.status === 'solution' && !dejaDeploye && (
              <button
                type="button"
                onClick={onDeployer}
                className="mt-4 min-h-touch w-full rounded-xl bg-emerald-600 px-6 font-semibold text-white hover:bg-emerald-700"
              >
                {T.tester.deployer}
              </button>
            )}

            {dejaDeploye && (
              <p className="mt-4 font-semibold text-emerald-800">
                <span aria-hidden="true">✓ </span>
                {T.tester.deploye}
              </p>
            )}
          </section>
        </>
      )}
    </div>
  );
}
